import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../U3400';
import { useU3400Context } from '../U3400Context';
import {
  doUpdateLayout,
  doUpsertMonthPreferences,
  luHandleAppiError,
  type U3400DashboardLayout,
  type U3400KpiUserLuPreferenceEffective,
  type U3400TileLogicalUnitCode,
} from '../U3400Service';
import {
  doFetchTileSeries,
  type U3400TileSeriesResult,
} from '../U3400TileSeriesService';

export function useU3400DashboardHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation(NS);
  const toast = useToast();
  const { layout, setLayout, upsertMonthPreferenceLocal } = useU3400Context();

  const handleError = useCallback(
    (error: unknown) => {
      const errorResult = U2000HandleApiError(
        error as Error,
        t,
        NS,
        luHandleAppiError,
      ) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
    [t, toast],
  );

  const updateLayout = useCallback(
    async (
      tiles: U3400TileLogicalUnitCode[],
      options?: { optimistic?: boolean },
    ) => {
      const previous = layout;
      if (options?.optimistic) {
        setLayout({ ...layout, tiles });
      }
      try {
        const body: {
          tiles: U3400TileLogicalUnitCode[];
          id?: string;
          version?: number;
        } = {
          tiles,
        };
        if (previous.id) body.id = previous.id;
        if (previous.version != null) body.version = previous.version;

        const response = await doUpdateLayout(axios, body);
        const next = response.data?.result as U3400DashboardLayout;
        setLayout(next);
        toast.success(t('toast.layoutSaved'));
        return next;
      } catch (error: unknown) {
        if (options?.optimistic) {
          setLayout(previous);
        }
        handleError(error);
        return null;
      }
    },
    [axios, handleError, layout, setLayout, t, toast],
  );

  const upsertMonthsTile = useCallback(
    async (logicalUnitCode: U3400TileLogicalUnitCode, monthsTile: number) => {
      try {
        const response = await doUpsertMonthPreferences(axios, {
          logicalUnitCode,
          monthsTile,
        });
        const next = response.data?.result as U3400KpiUserLuPreferenceEffective;
        upsertMonthPreferenceLocal(next);
        toast.success(t('toast.monthsSaved'));
        return next;
      } catch (error: unknown) {
        handleError(error);
        return null;
      }
    },
    [axios, handleError, t, toast, upsertMonthPreferenceLocal],
  );

  const fetchTileSeries = useCallback(
    async (code: U3400TileLogicalUnitCode, months: number) => {
      const response = await doFetchTileSeries(axios, code, months);
      return (response.data?.result as U3400TileSeriesResult) ?? null;
    },
    [axios],
  );

  return {
    updateLayout,
    upsertMonthsTile,
    fetchTileSeries,
    handleError,
  };
}
